/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import cancelTokenFromSignal from '../../helpers/cancelTokenFromSignal';
import getThumbnailUrls from '../../helpers/getThumbnailUrls';
import isAbortedRequest from '../../helpers/isAbortedRequest';
import { setThumbnailUrls, extendAssets } from '../helpers/assets';
import { setListingAssets } from './helpers/entityAdapter';
import { appendMissingGalleryAssets } from './helpers/appendMasterGalleryAssets';
import { setListingAssetsFetching } from './helpers/uiAdapter';
import { shouldSuppressMediaErrorToast } from './helpers/showChannelProductGoneToast';

export const fetchListingAssets = createAsyncThunk(
  'products/fetchListingAssets',
  async ({ masterProductId, channelId, listingProductId }, {
    extra: { Logger, Toast, sdk },
    getState,
    signal,
  }) => {
    try {
      const { team } = getState().user;
      const rootCollectionId = getState().collections?.collections?.my?._id;

      const { data } = await sdk.assets.search({
        tagId: rootCollectionId,
        from: 0,
        size: 250,
        products: listingProductId,
        sort: { type: 'custom' },
      }, cancelTokenFromSignal(signal));

      if (!data.images?.length) {
        return { masterProductId, channelId, listingProductId, assets: [] };
      }

      const assets = extendAssets(data.images);
      const thumbs = await getThumbnailUrls(assets.map(({ _id }) => _id));
      const assetsWithThumbs = setThumbnailUrls(assets, thumbs, team?.storageType === 's3');

      return {
        masterProductId,
        channelId,
        listingProductId,
        assets: assetsWithThumbs.map((asset) => ({
          ...asset,
          permissions: Object
            .keys(asset.permissions || {})
            .reduce((acc, key) => ({ ...acc, [key]: false }), {}),
        })),
      };
    } catch (error) {
      if (isAbortedRequest(error, signal)) throw error;
      Logger.log('UI', 'ToastProductsNotLoaded');
      if (!shouldSuppressMediaErrorToast()) {
        Toast('Products not loaded. Refresh and try again', { autoClose: false, type: 'error' });
      }
      Logger.error(new Error('Error listing assets fetching'), { error }, [
        'FetchListingAssetsFailed',
        { errorMessage: 'Listing assets can not be fetched from backend.' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(fetchListingAssets.pending, (state, { meta }) => {
      const { listingProductId } = meta.arg;
      setListingAssetsFetching(state, listingProductId, true);
    })
    .addCase(fetchListingAssets.fulfilled, (state, { payload, meta }) => {
      const { listingProductId, assets } = payload;
      const resolvedListingProductId = listingProductId || meta.arg.listingProductId;
      const nextAssets = appendMissingGalleryAssets(assets, meta.arg?.mergeAssets);
      setListingAssets(state, resolvedListingProductId, nextAssets);
      setListingAssetsFetching(state, resolvedListingProductId, false);
    })
    .addCase(fetchListingAssets.rejected, (state, { meta }) => {
      const { listingProductId } = meta.arg;
      setListingAssetsFetching(state, listingProductId, false);
    });
};
